import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { BillingInfoFormManager } from '@ctct/application-layer';
import { type BillingStrategyQuery } from '@ctct/domain-layer';
import type { BillingInfoFormHooks, BillingInfoFormPageStore } from '@ctct/state-client-layer';
import { useAllCountries, useBillingAccount, useBillingInfo, useCustomerInfo } from '@ctct/state-server-layer';

import { useWidgetLifecycle } from '../../hooks/useWidgetLifecycle';

interface Props {
  initializer: Pick<BillingInfoFormManager, 'billingInfoFormInit'>;
  pageStore: BillingInfoFormPageStore;
  hooks: BillingInfoFormHooks;
  billingStrategy: BillingStrategyQuery;
  flags?: Parameters<BillingInfoFormManager['billingInfoFormInit']>[0]['flags'];
}

export const useBillingInfoFormWidget = ({ initializer, pageStore, hooks, billingStrategy, flags = {} }: Props) => {
  const { t } = useTranslation();

  const countriesQuery = useAllCountries();
  const billingInfoQuery = useBillingInfo();
  const billingAccountQuery = useBillingAccount();
  const customerInfoQuery = useCustomerInfo();

  const { skipFormPrefill } = flags;

  const componentProps = useMemo(() => {
    const initedManagerReturnValue = initializer.billingInfoFormInit({
      pageStore,
      queries: [countriesQuery, billingInfoQuery, billingAccountQuery, customerInfoQuery],
      t,
      flags: { skipFormPrefill },
    });

    return {
      ...initedManagerReturnValue,
      hooks,
      billingStrategy: billingStrategy as BillingStrategyQuery,
    };
  }, [
    initializer,
    pageStore,
    countriesQuery,
    billingInfoQuery,
    billingAccountQuery,
    customerInfoQuery,
    t,
    hooks,
    billingStrategy.data,
    billingStrategy.isPending,
    skipFormPrefill,
  ]);

  useWidgetLifecycle(componentProps.status, componentProps.lifecycle);

  return componentProps;
};

export type BillingInfoFormWidgetProps = ReturnType<typeof useBillingInfoFormWidget>;
